import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useCreateMasterUploadMutation, useGetAccountQuery } from '../../../../redux/api/fymAPI';
import { CreateMasterUploadResponse } from '../../../../redux/api/fymAPI.types';
import { catalogSongMessages } from './catalogSongMessages';
import { checkMasterFile, MAX_MASTER_MB } from './masterFile';
import { masterUploadRefusal as refusal } from './masterUploadRefusal';
import { masterUploadErrorMessage, reportMasterUploadError } from './masterUploadErrors';
import { usePendingMasterComplete } from './pendingMasterCompletes';
import { useMasterPartsSender } from './useMasterPartsSender';

/** What an upload did: the master is saved, the rights box is needed first, or it failed. */
export type MasterUploadOutcome = 'saved' | 'rightsRequired' | 'failed';

interface UseMasterUploadProps {
  accountId: number;
  /** The catalog row. Undefined while the row does not exist yet: pass the uuid to `upload` then. */
  itemUuid?: string;
}

/**
 * Uploads the master of a catalog row straight to S3 (D29: Cloudflare refuses a body over 100 MB).
 *
 * 1. `POST .../catalog/:uuid/master/uploads` checks the size, the type and the rights (design g1)
 *    and gives one presigned address per part.
 * 2. The parts go to S3 one at a time (`uploadMasterParts`), with no app headers. A failed part
 *    goes again up to 3 times.
 * 3. `POST .../master/uploads/:uploadId/complete` sends the ETags back; the API saves the master.
 *
 * The create and complete calls go through `customBaseQuery`, so an expired session token is
 * refreshed there. When a part fails for good, `cancel` runs (the Stop button), or the screen
 * closes while the parts go up, `DELETE .../master/uploads/:uploadId` aborts the S3 upload.
 *
 * Once every part is in S3, nothing stops the upload: the complete call runs to its end even when
 * the screen closes, so the master is never lost half way. `pendingMasterCompletes` tells a screen
 * that opens the row meanwhile that the master is still finishing (`finishingName`).
 *
 * `progress` is the percent of the bytes sent. `canStop` is true while the parts go up.
 * `needsRights` is true while the account has not confirmed the rights, and after a 400
 * `M24_MASTER_RIGHTS_REQUIRED`. `upload` resolves with the outcome; `error` holds the text of a
 * failure.
 */
export function useMasterUpload({ accountId, itemUuid }: UseMasterUploadProps) {
  const intl = useIntl();
  const { data: accountResponse } = useGetAccountQuery({ accountId });
  const [createUpload] = useCreateMasterUploadMutation();
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Set from the answers of this page, so the box goes away (or comes back) at once.
  const [rightsAnswer, setRightsAnswer] = useState<'confirmed' | 'required' | null>(null);
  const finishingName = usePendingMasterComplete(itemUuid);
  const { sendParts, cancel, stops, isCompleting } = useMasterPartsSender({ accountId, setProgress, setError });

  const accountConfirmed = accountResponse?.account.masterRightsConfirmed === true;
  const needsRights = rightsAnswer === null ? !accountConfirmed : rightsAnswer === 'required';

  const upload = useCallback(
    async (file: File, rightsConfirmed: boolean, uuid: string | undefined = itemUuid): Promise<MasterUploadOutcome> => {
      setError(null);
      const problem = checkMasterFile(file);
      if (problem !== null) {
        setError(
          problem === 'tooBig'
            ? intl.formatMessage(catalogSongMessages.tooBig, { max: MAX_MASTER_MB })
            : intl.formatMessage(catalogSongMessages.wrongType)
        );
        return 'failed';
      }
      if (uuid === undefined) {
        return 'failed';
      }
      setProgress(0);
      const stopsAtStart = stops.current;
      let created: CreateMasterUploadResponse;
      try {
        created = await createUpload({
          accountId,
          itemUuid: uuid,
          fileName: file.name,
          contentType: file.type === '' ? 'application/octet-stream' : file.type,
          sizeBytes: file.size,
          rightsConfirmed: rightsConfirmed ? true : undefined,
        }).unwrap();
      } catch (failure) {
        setProgress(null);
        const { reason, message } = refusal(failure);
        if (reason === 'M24_MASTER_RIGHTS_REQUIRED') {
          setRightsAnswer('required');
          return 'rightsRequired';
        }
        reportMasterUploadError('create', failure, file);
        setError(message ?? masterUploadErrorMessage(intl, failure));
        return 'failed';
      }
      const outcome = await sendParts(file, uuid, created, stopsAtStart);
      setProgress(null);
      if (outcome === 'saved') {
        setRightsAnswer('confirmed');
      }
      return outcome;
    },
    [accountId, createUpload, intl, itemUuid, sendParts, stops]
  );

  const canStop = progress !== null && !isCompleting;

  return { upload, cancel, canStop, progress, error, needsRights, finishingName };
}

export type MasterUpload = ReturnType<typeof useMasterUpload>;
